import {
  Star,
  StarHalf,
  Heart,
  ExternalLink,
  Mail,
  Share2,
  Instagram,
  Facebook,
} from "lucide-react";

import { SanitizedTag } from "../utils";

export const SignatureProfileHero = ({ profile }: { profile: any }) => {
  const artistFullName = [profile?.artistFirstName, profile?.artistLastName]
    .filter(Boolean)
    .join(" ");

  const reviews = (profile?.reviews as { rank?: number }[]) || [];
  const reviewCount = reviews.length;
  const averageRating =
    reviewCount > 0
      ? reviews.reduce((sum, review) => sum + (review?.rank || 0), 0) /
        reviewCount
      : 0;

  const MAX_STARS = 5;
  const fullStars = Math.floor(averageRating);
  const hasHalfStar = averageRating - fullStars >= 0.5 && fullStars < MAX_STARS;
  const emptyStars = MAX_STARS - fullStars - (hasHalfStar ? 1 : 0);

  const renderFullStar = (key: string | number) => (
    <Star
      key={`full-${key}`}
      size={20}
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={2}
    />
  );

  const renderHalfStar = () => (
    <StarHalf
      key="half"
      size={20}
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={2}
    />
  );

  const renderEmptyStar = (key: string | number) => (
    <Star
      key={`empty-${key}`}
      size={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    />
  );

  const activeSinceYear = profile?.activeSince
    ? new Date(profile.activeSince).getFullYear()
    : undefined;

  const hasPicture = Boolean(profile?.artistPictureLargeUrl);
  const hasName = Boolean(artistFullName && artistFullName.trim().length > 0);
  const hasTags =
    Array.isArray(profile?.styleTags) && profile.styleTags.length > 0;
  const hasLocation = Boolean(profile?.location);
  const hasActiveSince = Boolean(activeSinceYear);
  const showMetaRow = hasTags || hasLocation || hasActiveSince;
  const hasAbout = Boolean(profile?.about);
  const instagramLink: string | undefined = profile?.instagrams?.[0]?.visitLink;
  const facebookLink: string | undefined = profile?.facebooks?.[0]?.visitLink;
  const showSocial = Boolean(instagramLink || facebookLink);
  const followArtistLink: string | undefined = profile?.followArtistLink;
  const visitWebsiteLink: string | undefined = profile?.visitWebsiteLink;
  const contactLink: string | undefined = profile?.contactLink;

  return (
    <section className="pt-32 pb-16 px-4 md:pt-32 md:pb-24">
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
          {hasPicture ? (
            <div className="lg:w-2/5">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-artist-blue/20 to-artist-red/20 rounded-lg transform rotate-3" />
                {/* 2. Signature Profile artist picture (base64 encoded) */}
                <img
                  alt={artistFullName || "Artist picture"}
                  className="rounded-lg shadow-xl relative z-10 w-full max-w-md mx-auto"
                  src={profile.artistPictureLargeUrl}
                />
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-artist-yellow/10 rounded-full blur-xl" />
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-artist-blue/10 rounded-full blur-xl" />
              </div>
            </div>
          ) : null}
          <div className={hasPicture ? "lg:w-3/5" : "w-full"}>
            {/* 3. Signature Profile artist name */}
            {hasName ? (
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-artist-charcoal">
                {artistFullName}
              </h1>
            ) : null}
            {/* 4. Signature Profile artist reviews. You should calculate the average rating from the reviews */}
            {reviewCount > 0 ? (
              <div className="flex items-center gap-4 mb-4">
                <div className="star-rating flex items-center">
                  {Array.from({ length: fullStars }).map((_, index) =>
                    renderFullStar(index)
                  )}
                  {hasHalfStar && renderHalfStar()}
                  {Array.from({ length: emptyStars }).map((_, index) =>
                    renderEmptyStar(index)
                  )}
                </div>
                <span className="text-muted-foreground">
                  {`${averageRating.toFixed(1)} (${reviewCount} review${
                    reviewCount === 1 ? "" : "s"
                  })`}
                </span>
              </div>
            ) : null}
            {/* 5. Signature Profile artist style tags */}
            {showMetaRow ? (
              <div className="mb-6 flex items-center text-sm text-muted-foreground">
                {hasTags
                  ? profile.styleTags.map((tag: string) => (
                      <span
                        key={tag}
                        className="mr-3 bg-artist-blue/10 text-artist-blue py-1 px-3 rounded-full"
                      >
                        {tag}
                      </span>
                    ))
                  : null}
                {/* 6. Signature Profile location */}
                {hasLocation ? (
                  <span className="mr-3">{profile.location}</span>
                ) : null}
                {/* 7. Signature Profile active since */}
                {hasActiveSince ? (
                  <span>
                    Active since&nbsp;
                    {activeSinceYear}
                  </span>
                ) : null}
              </div>
            ) : null}
            {hasAbout ? (
              <SanitizedTag
                html={profile.about}
                tag="p"
                className="text-lg mb-6 max-w-2xl"
              />
            ) : null}
            <div className="flex flex-wrap gap-3">
              {followArtistLink ? (
                <a
                  href={followArtistLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary-foreground h-10 px-4 py-2 rounded-full bg-artist-accent hover:bg-artist-accent/90"
                >
                  <Heart size={18} className="mr-2" />
                  Follow Artist
                </a>
              ) : null}
              {visitWebsiteLink ? (
                <a
                  href={visitWebsiteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary-foreground h-10 px-4 py-2 rounded-full bg-purple-500 hover:bg-purple-600"
                >
                  <ExternalLink size={18} className="mr-2" />
                  Visit Website
                </a>
              ) : null}
              {contactLink ? (
                <a
                  href={contactLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 rounded-full"
                >
                  <Mail size={18} className="mr-2" />
                  Contact
                </a>
              ) : null}
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-secondary text-secondary-foreground hover:bg-secondary/80 h-10 w-10 rounded-full"
              >
                <Share2 size={18} />
              </button>
            </div>
            {showSocial ? (
              <div className="flex gap-4 mt-4">
                {instagramLink ? (
                  <a
                    href={instagramLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-artist-accent transition-colors"
                  >
                    <Instagram size={20} />
                    <span className="sr-only">Instagram</span>
                  </a>
                ) : null}
                {facebookLink ? (
                  <a
                    href={facebookLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-artist-accent transition-colors"
                  >
                    <Facebook size={20} />
                    <span className="sr-only">Facebook</span>
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignatureProfileHero;
